// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {type FC, useContext} from 'react';

import {
  formatAsDollar,
  getFormattedBillingDate,
} from '../../../pages/Billing/AccountSettings/BillingTab/PlanCard/PlanCardComponents';
import {
  createDismissibleBanner,
  DEFAULT_BANNER_SHOW_AFTER,
} from '../../../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {differenceInCalendarDaysUTC, isAfter} from '../../../util/date';
import {useRampInferenceAsProduct} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useGetInferenceUsageDetails} from '../../../util/usage/inference';
import {
  AccountStatusBanner,
  AccountStatusBannerBoldText,
  AccountStatusBannerFlexWrapper,
  AccountStatusBannerTextWrapper,
} from '../../AccountStatusBanner';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {AccountPlanType} from '../../Search/SearchNav/types';
import {type InferenceExceededLimitBannerProps} from './types';
import {
  ActivateCTA,
  ContactSalesCTA,
  ContactSupportCTA,
  UpgradePlanCTA,
} from './utils';

const TODAY = new Date();

export const InferenceExceededLimitBannerGlobalList: FC = () => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const rampInferenceAsProduct = useRampInferenceAsProduct();

  const {
    billingPeriodStart,
    billingPeriodEnd,
    isUsagePaid,
    usage,
    limits,
    enabled,
    loading,
    error,
  } = useGetInferenceUsageDetails(selectedAccount);

  if (loading || error || selectedAccount == null) {
    return null;
  }
  if (limits.inferenceSafetyLimit === 0 || !enabled) {
    return null;
  }

  const organizationId = selectedAccount.id ?? '';

  let banner = null;

  switch (selectedAccount.accountPlanType) {
    case AccountPlanType.Free:
    case AccountPlanType.ThirtyDayTrial:
    case AccountPlanType.Academic:
      if (usage >= limits.inferenceSafetyLimit && !isUsagePaid) {
        if (rampInferenceAsProduct) {
          banner = (
            <InferenceExceededLimitBanner
              organizationId={organizationId}
              exceededLimitBoldStatusMsg="Out of W&B Inference credits."
              exceededLimitStatusMsg="To continue using W&B Inference, activate pay-as-you-go on your inference plan."
              exceededLimitCTA={<ActivateCTA />}
              planName={selectedAccount.accountPlanType}
            />
          );
        } else {
          banner = (
            <InferenceExceededLimitBanner
              organizationId={organizationId}
              exceededLimitBoldStatusMsg="Out of W&B Inference credits."
              exceededLimitStatusMsg="To continue using Inference, please upgrade your plan."
              exceededLimitCTA={<UpgradePlanCTA />}
              planName={selectedAccount.accountPlanType}
            />
          );
        }
      }
      break;
    case AccountPlanType.Teams:
    case AccountPlanType.Pro:
      if (usage >= limits.inferenceSafetyLimit) {
        banner = (
          <InferenceExceededLimitBanner
            organizationId={organizationId}
            exceededLimitBoldStatusMsg={`You've hit your ${formatAsDollar(
              limits.inferenceSafetyLimit / 100
            )} monthly W&B Inference spend limit.`}
            exceededLimitStatusMsg={`Want a higher cap? Talk to support or wait for your limit to reset on ${getFormattedBillingDate(
              billingPeriodEnd
            )}`}
            exceededLimitCTA={<ContactSupportCTA />}
            planName={selectedAccount.accountPlanType}
          />
        );
      }
      break;
    case AccountPlanType.Enterprise:
      if (
        limits.inferenceSafetyLimit === 0 ||
        isAfter(TODAY, billingPeriodEnd) ||
        isAfter(billingPeriodStart, TODAY)
      ) {
        break; // no banner if outside the billing period or no limit set
      } else if (usage >= limits.inferenceSafetyLimit) {
        banner = (
          <InferenceExceededLimitBanner
            organizationId={organizationId}
            exceededLimitBoldStatusMsg={`You've hit your ${formatAsDollar(
              limits.inferenceSafetyLimit / 100
            )} W&B Inference spend limit.`}
            exceededLimitStatusMsg={`Want a higher cap? Talk to support.`}
            exceededLimitCTA={<ContactSupportCTA />}
            planName={selectedAccount.accountPlanType}
          />
        );
      } else if (
        usage > 0 &&
        differenceInCalendarDaysUTC(billingPeriodEnd, TODAY) <= 30
      ) {
        banner = (
          <InferenceExceededLimitBanner
            organizationId={organizationId}
            exceededLimitBoldStatusMsg={`You have ${differenceInCalendarDaysUTC(
              billingPeriodEnd,
              TODAY
            )} days remaining in your W&B Inference trial.`}
            exceededLimitStatusMsg={`Once your trial expires access to W&B Inference will be restricted.`}
            exceededLimitCTA={<ContactSalesCTA />}
            planName={selectedAccount.accountPlanType}
          />
        );
      } else if (usage >= limits.inferenceSafetyLimit * 0.9) {
        banner = (
          <InferenceExceededLimitBanner
            organizationId={organizationId}
            exceededLimitBoldStatusMsg={`Almost out of W&B Inference credits.`}
            exceededLimitStatusMsg={`To continue using W&B Inference without disruption, talk to sales.`}
            exceededLimitCTA={<ContactSalesCTA />}
            planName={selectedAccount.accountPlanType}
            showDismissButton={false}
          />
        );
      }
      break;
  }

  return (
    <Tailwind>
      <div className="space-y-8">{banner}</div>
    </Tailwind>
  );
};

export const InferenceExceededLimitBanner =
  createDismissibleBanner<InferenceExceededLimitBannerProps>({
    id: ({organizationId}) => {
      return `inference-exceeded-limit-banner-${organizationId}`;
    },
    showAfter: DEFAULT_BANNER_SHOW_AFTER,
    Component: ({
      organizationId,
      exceededLimitBoldStatusMsg,
      exceededLimitStatusMsg,
      exceededLimitCTA,
      planName,
      showDismissButton = true,
      onDismiss,
    }) => {
      return (
        <Tailwind>
          <AccountStatusBanner
            eventData={{
              location: `exceeded 100% for Inference alert banner`,
              organizationId,
              resource: 'Inference',
              planName,
            }}>
            <AccountStatusBannerFlexWrapper>
              <div>
                <Icon name="info" />
              </div>
              <AccountStatusBannerTextWrapper>
                <span>
                  <AccountStatusBannerBoldText>
                    {exceededLimitBoldStatusMsg}
                  </AccountStatusBannerBoldText>{' '}
                  <span>{exceededLimitStatusMsg}</span>
                </span>
              </AccountStatusBannerTextWrapper>
            </AccountStatusBannerFlexWrapper>

            {exceededLimitCTA}
            {showDismissButton ? (
              <Button
                variant="ghost"
                onClick={onDismiss}
                className="text-moon-800 hover:bg-white/[.20]">
                Dismiss
              </Button>
            ) : null}
          </AccountStatusBanner>
        </Tailwind>
      );
    },
  });
